import React, { useEffect, useMemo, useState } from "react"

import { css } from "@emotion/react"
import { Radio, Space } from "antd"
import type { RadioChangeEvent } from "antd/lib/radio"
import type {
    DeepMap,
    FieldError,
    FieldValues,
    UseFormRegister,
    UseFormSetValue,
    UseFormUnregister,
} from "react-hook-form"

import type { IIndexable } from "@common-shared/types/general.types"

import type { IRadioItem } from "@front-shared/types/objects.types"

import { NIRVANA_COLORS } from "../config/theme.config"
import { InputErrorElement } from "./inputError.element"

export const RadioElement: React.FunctionComponent<
    IRadioElementProps
> = props => {
    const [currentValue, setCurrentValue] = useState<any>(undefined)
    const [defaultProcessed, setDefaultProcessed] = useState<boolean>(false)

    const elementFullName = useMemo<string>(() => {
        return `${props.fieldGroupName ? `${props.fieldGroupName}.` : ""}${
            props.name
        }`
    }, [props.name, props.fieldGroupName])

    useEffect(() => {
        if (props.reactHookFormRegister) {
            props.reactHookFormRegister(
                elementFullName,
                props.reactHookValidations || {},
            )
        }

        return () => {
            props.reactHookFormUnregister?.(elementFullName)
        }
    }, [props.reactHookFormRegister])

    useEffect(() => {
        if (
            defaultProcessed ||
            typeof props.defaultValue === "undefined" ||
            props.defaultValue === currentValue
        ) {
            return
        }

        setDefaultProcessed(true)
        onChange(props.defaultValue)
    }, [props.defaultValue])

    const onChangeEvent = (event: RadioChangeEvent) => {
        onChange(event.target.value)
    }

    const onChange = (newValue: any) => {
        if (props.onChange) {
            props.onChange(newValue)
        }

        if (props.reactHookFormSet) {
            props.reactHookFormSet(elementFullName, newValue, {
                shouldValidate: !!props.shouldValidate,
            })
        }

        setCurrentValue(newValue)
    }

    const handleUncheck = (event: any) => {
        if (props.allowDeselection && event.target?.value === currentValue) {
            setCurrentValue(undefined)
        }
    }

    return (
        <div
            className={`
                relative
                ${props.className}
            `}
        >
            <Radio.Group
                onChange={onChangeEvent}
                value={currentValue}
                disabled={props.disabled}
            >
                <Space direction={props.direction || "vertical"}>
                    {props.items.map((item, index) => (
                        <Radio
                            key={index}
                            value={item.value}
                            disabled={item.disabled}
                            className={item.className}
                            onClick={handleUncheck}
                        >
                            <span css={styles.label}>{item.label}</span>
                        </Radio>
                    ))}
                </Space>
            </Radio.Group>

            {props.reactHookErrors?.[props.name] && (
                <InputErrorElement
                    text={
                        (props.reactHookErrors[props.name] as FieldError)
                            .message || "Error"
                    }
                />
            )}
        </div>
    )
}

const styles = {
    label: css({
        color: NIRVANA_COLORS.brand.primary,
    }),
}

interface IRadioElementProps {
    className?: string
    name: string
    fieldGroupName?: string
    items: IRadioItem[]
    reactHookFormRegister?: UseFormRegister<FieldValues>
    reactHookFormUnregister?: UseFormUnregister<FieldValues>
    reactHookFormSet?: UseFormSetValue<FieldValues>
    reactHookErrors?: DeepMap<FieldValues, FieldError>
    reactHookValidations?: IIndexable
    shouldValidate?: boolean
    defaultValue?: any
    disabled?: boolean
    onChange?: (newValue: boolean) => void
    direction?: "vertical" | "horizontal"
    allowDeselection?: boolean
}
